import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Collapse, alpha, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import { MarketplaceOrderCard, type MarketplaceOrderCardProps } from "./MarketplaceOrderCard";

const DISMISS_FLASH_MS = 380;
const DISMISS_COLLAPSE_MS = 420;

type DismissableMarketplaceOrderCardProps = MarketplaceOrderCardProps & {
  isDismissing?: boolean;
  onDismissComplete?: () => void;
};

/** Card de pedido com animação de remoção (flash vermelho + lixeira + colapso suave). */
export function DismissableMarketplaceOrderCard({
  isDismissing = false,
  onDismissComplete,
  ...cardProps
}: DismissableMarketplaceOrderCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    if (!isDismissing) {
      setExpanded(true);
      setShowOverlay(false);
      return;
    }

    setShowOverlay(true);
    const collapseTimer = window.setTimeout(() => {
      setExpanded(false);
    }, DISMISS_FLASH_MS);

    return () => window.clearTimeout(collapseTimer);
  }, [isDismissing]);

  return (
    <Collapse
      in={expanded}
      timeout={DISMISS_COLLAPSE_MS}
      onExited={onDismissComplete}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          ...(showOverlay && {
            animation: `orderCancelFlash ${DISMISS_FLASH_MS}ms ease-out forwards`,
            "@keyframes orderCancelFlash": {
              "0%": {
                boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}`,
              },
              "40%": {
                boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.6)}`,
              },
              "100%": {
                boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, 0.35)}`,
              },
            },
          }),
        }}
      >
        <Box
          sx={{
            ...(showOverlay && {
              filter: "grayscale(0.4) brightness(0.9)",
              transition: `filter ${DISMISS_FLASH_MS}ms ease-out`,
            }),
          }}
        >
          <MarketplaceOrderCard {...cardProps} />
        </Box>

        {showOverlay ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.error.main, 0.45),
              animation: `orderTrashOverlayIn ${DISMISS_FLASH_MS * 0.7}ms ease-out forwards`,
              "@keyframes orderTrashOverlayIn": {
                "0%": { opacity: 0 },
                "100%": { opacity: 1 },
              },
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            <DeleteOutlineIcon
              sx={{
                fontSize: 52,
                color: "common.white",
                filter: `drop-shadow(0 2px 8px ${alpha(theme.palette.common.black, 0.35)})`,
                animation: `orderTrashPop ${DISMISS_FLASH_MS * 0.65}ms cubic-bezier(0.34, 1.56, 0.64, 1) 0.08s both`,
                "@keyframes orderTrashPop": {
                  "0%": { opacity: 0, transform: "scale(0.3)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
              }}
            />
          </Box>
        ) : null}
      </Box>
    </Collapse>
  );
}
